import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Content,
  Header,
  Page,
  Progress,
  InfoCard,
  ContentHeader,
  SupportButton,
  CodeSnippet,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { formatDistance } from 'date-fns';
import { DeploymentPipeline } from './DeploymentPipeline';

interface TaskStep {
  id: string;
  name: string;
  status: 'completed' | 'failed' | 'running';
  startTime: string;
  endTime?: string;
  duration: number;
  output: string;
  error?: string;
}

interface TaskDetail {
  id: string;
  templateName: string;
  status: 'completed' | 'failed' | 'running';
  createdAt: string;
  completedAt?: string;
  duration?: number;
  createdBy: string;
  entityRef?: string;
  prUrl?: string;
  actionsRunUrl?: string;
  argoAppName?: string;
  argoInstance?: string;
  platform?: string;
  error?: string;
  steps: TaskStep[];
  spec?: {
    parameters?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

const getStatusChipColor = (
  status: string,
): 'success' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon style={{ color: '#4caf50' }} />;
    case 'failed':
      return <ErrorIcon style={{ color: '#f44336' }} />;
    case 'running':
      return <PlayArrowIcon style={{ color: '#2196f3' }} />;
    default:
      return null;
  }
};

const formatDuration = (durationMs: number | undefined, from?: string, to?: string) => {
  const ms = durationMs ?? (from && to
    ? new Date(to).getTime() - new Date(from).getTime()
    : undefined);
  if (!ms || ms <= 0) return '—';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const renderParamValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return <em>—</em>;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') {
    return (
      <CodeSnippet
        language="json"
        text={JSON.stringify(value, null, 2)}
        showLineNumbers={false}
      />
    );
  }
  return String(value);
};

export const TaskDetailPage = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const load = useCallback(async () => {
    if (!taskId) {
      setError('Task ID is required');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = await discoveryApi.getBaseUrl('tasks');
      const response = await fetchApi.fetch(`${baseUrl}/user-tasks/${taskId}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TaskDetail = await response.json();
      setTask(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch task details');
    } finally {
      setLoading(false);
    }
  }, [taskId, discoveryApi, fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Task Details" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error || !task) {
    return (
      <Page themeId="tool">
        <Header title="Task Details" />
        <Content>
          <Typography color="error">Error: {error || 'Task not found'}</Typography>
          <Box mt={2}>
            <Button variant="contained" color="primary" onClick={load} style={{ marginRight: 8 }}>
              Retry
            </Button>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tasks')}
              variant="outlined"
            >
              Back to Tasks
            </Button>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title={`Task: ${task.templateName}`}
        subtitle={`ID: ${task.id}`}
      />
      <Content>
        <ContentHeader title="Task Details">
          <SupportButton>Review the execution details of your template run.</SupportButton>
          <Button variant="contained" color="primary" onClick={load}>Refresh</Button>
        </ContentHeader>

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tasks')}
          style={{ marginBottom: 16 }}
        >
          Back to Tasks
        </Button>

        <InfoCard title="Task Overview">
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="body1" style={{ marginRight: 16 }}>
                <strong>Status:</strong>
              </Typography>
              <Chip
                label={task.status}
                size="small"
                color={getStatusChipColor(task.status)}
                variant="filled"
              />
            </Box>

            <Typography variant="body1" style={{ marginBottom: 16 }}>
              <strong>Created:</strong>{' '}
              <Tooltip title={new Date(task.createdAt).toLocaleString()}>
                <span>{formatDistance(new Date(task.createdAt), new Date(), { addSuffix: true })}</span>
              </Tooltip>
            </Typography>

            {task.completedAt && (
              <Typography variant="body1" style={{ marginBottom: 16 }}>
                <strong>Completed:</strong>{' '}
                <Tooltip title={new Date(task.completedAt).toLocaleString()}>
                  <span>{formatDistance(new Date(task.completedAt), new Date(), { addSuffix: true })}</span>
                </Tooltip>
              </Typography>
            )}

            <Typography variant="body1" style={{ marginBottom: 16 }}>
              <strong>Duration:</strong>{' '}
              {formatDuration(task.duration, task.createdAt, task.completedAt)}
            </Typography>

            {task.entityRef && (
              <Typography variant="body1" style={{ marginBottom: 16 }}>
                <strong>Entity:</strong> {task.entityRef}
              </Typography>
            )}

            {task.error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {task.error}
              </Alert>
            )}
          </Box>
        </InfoCard>

        {task.spec?.parameters && Object.keys(task.spec.parameters).length > 0 && (
          <Box mt={3}>
            <InfoCard title="Input Parameters">
              <Table size="small">
                <TableBody>
                  {Object.entries(task.spec.parameters).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell style={{ fontWeight: 600, whiteSpace: 'nowrap', width: '30%' }}>
                        {key}
                      </TableCell>
                      <TableCell>{renderParamValue(value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </InfoCard>
          </Box>
        )}

        <Box mt={3}>
          <DeploymentPipeline
            taskStatus={task.status}
            prUrl={task.prUrl}
            actionsRunUrl={task.actionsRunUrl}
            entityRef={task.entityRef}
            argoAppName={task.argoAppName}
            argoInstance={task.argoInstance}
            platform={task.platform}
          />
        </Box>

        <Box mt={3}>
          <Typography variant="h5" gutterBottom>
            Execution Steps ({task.steps.length})
          </Typography>

          {task.steps.map(step => (
            <Accordion key={step.id} defaultExpanded={step.status === 'failed'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" width="100%">
                  <Box mr={2}>
                    {getStatusIcon(step.status)}
                  </Box>
                  <Typography variant="h6">{step.name}</Typography>
                  <Box flexGrow={1} />
                  <Chip
                    label={step.status}
                    size="small"
                    color={getStatusChipColor(step.status)}
                    variant="filled"
                    style={{ marginRight: 16 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDuration(step.duration)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box width="100%">
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Started:</strong> {new Date(step.startTime).toLocaleString()}
                    </Typography>
                    {step.endTime && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Completed:</strong> {new Date(step.endTime).toLocaleString()}
                      </Typography>
                    )}
                  </Box>

                  {step.error && (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                      Error: {step.error}
                    </Alert>
                  )}

                  <CodeSnippet language="bash" text={step.output} showLineNumbers />
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Content>
    </Page>
  );
};
